// Navigation reference for the shared layout.
//
// Single source of truth that maps every menu entry to its { pageId, slug,
// label }. The layout's navigation routes by **slug** (never by pageId), so
// generated page routes must be keyed on these slugs to match. Keep this file
// in sync as pages are generated — add/update entries, never hand-maintain the
// nav inline.
//
// Derived from the design reference's `menuData` (declared in the raw layout
// shell) joined with the page slugs in RawHTMLDesign/PageDesignMap.json.

export const menuData = {
  children: [
    {
      label: 'Member Portal',
      icon: 'fa fa-user',
      children: [
        { label: 'File Reimbursement Claim', pageId: 72854, slug: 'file-reimbursement-claim', icon: 'fa fa-paper-plane' },
        { label: 'Benefits & Settlement Breakdown', pageId: 72870, slug: 'explanation-of-benefits-settlement-breakdown', icon: 'fa fa-file-medical' },
        { label: 'Request Outpatient LOA', pageId: 72853, slug: 'request-cashless-loa', icon: 'fa fa-notes-medical' },
      ],
    },
    {
      label: 'Corporate Portal',
      icon: 'fa fa-building',
      children: [
        { label: 'HR Dashboard & Enrollments', pageId: 72857, slug: 'corporate-hr-portal-dashboard', icon: 'fa fa-users' },
      ],
    },
    {
      label: 'Carrier Portal',
      icon: 'fa fa-briefcase',
      children: [
        // pageId 72883 is UI-assigned: this page was wired as a route but had no
        // entry in the design's menuData / PageDesignMap, so it had no nav link.
        { label: 'Program Performance Dashboard', pageId: 72883, slug: 'carrier-program-performance-dashboard', icon: 'fa fa-chart-line' },
      ],
    },
    {
      label: 'Policy & Rules Administration',
      icon: 'fa fa-cogs',
      children: [
        { label: 'User Management', pageId: 72887, slug: 'user-management', icon: 'fa fa-users-cog' },
        { label: 'Member Administration', pageId: 72851, slug: 'member-portal-dashboard', icon: 'fa fa-id-card-alt' },
        { label: 'Access Control, MFA & Compliance', pageId: 72871, slug: 'access-control-mfa-compliance-administration', icon: 'fa fa-user-shield' },
        { label: 'AI / LLM Provider Settings', pageId: 72884, slug: 'ai-llm-provider-settings', icon: 'fa fa-robot' },
        { label: 'Policy & Benefits Configuration', pageId: 72861, slug: 'policy-benefits-configuration', icon: 'fa fa-sliders-h' },
        { label: 'Workflow Rules & SLA Configuration', pageId: 72868, slug: 'workflow-rules-escalations-sla-configuration', icon: 'fa fa-stream' },
      ],
    },
    {
      label: 'Provider Portal',
      icon: 'fa fa-hospital',
      children: [
        { label: 'Eligibility & Directory', pageId: 72852, slug: 'provider-portal-dashboard-eligibility', icon: 'fa fa-hospital-user' },
        { label: 'Provider Performance Monitor', pageId: 72875, slug: 'provider-performance-utilization-service-quality-m', icon: 'fa fa-chart-line' },
        { label: 'Provider Claims Submission', pageId: 72855, slug: 'provider-claims-submission', icon: 'fa fa-file-invoice-dollar' },
        { label: 'Inpatient Billing Submission', pageId: 72863, slug: 'inpatient-hospital-billing-submission', icon: 'fa fa-file-invoice' },
        { label: 'Walk-in LOA Submission', pageId: 72856, slug: 'submit-cashless-loa-walk-in', icon: 'fa fa-id-card' },
        { label: 'Submit Dental Pre-Auth', pageId: 72885, slug: 'submit-dental-pre-authorization', icon: 'fa fa-tooth' },
        { label: 'Submit Veterinary Pre-Auth', pageId: 72886, slug: 'submit-veterinary-pre-authorization', icon: 'fa fa-paw' },
        { label: 'Inpatient GOP Request', pageId: 72866, slug: 'inpatient-admission-gop-request', icon: 'fa fa-procedures' },
      ],
    },
    {
      label: 'Claims Operations',
      icon: 'fa fa-briefcase-medical',
      children: [
        { label: 'Adjudication Workspace', pageId: 72860, slug: 'claims-adjudication-workspace', icon: 'fa fa-balance-scale' },
        { label: 'Claims Auditing Inbox', pageId: 72862, slug: 'claims-inbox-auditing-queue', icon: 'fa fa-inbox' },
        { label: 'Appeals & Grievances', pageId: 72867, slug: 'claims-appeal-grievance-resolution', icon: 'fa fa-gavel' },
        { label: 'Clinical Guidelines Review', pageId: 72877, slug: 'clinical-guidelines-decision-support-review', icon: 'fa fa-stethoscope' },
        { label: 'Coordination of Benefits Review', pageId: 72878, slug: 'coordination-of-benefits-review', icon: 'fa fa-project-diagram' },
        { label: 'FWA & Duplicate Detection', pageId: 72879, slug: 'fraud-waste-abuse-duplicate-claim-detection', icon: 'fa fa-search-dollar' },
        { label: 'OCR Intake Review', pageId: 72880, slug: 'ocr-claim-intake-extraction-review', icon: 'fa fa-file-alt' },
      ],
    },
    {
      label: 'Pre-Authorization Operations',
      icon: 'fa fa-user-md',
      children: [
        { label: 'Clinical Pre-Auth Queue', pageId: 72858, slug: 'clinical-pre-auth-queue-review', icon: 'fa fa-user-md' },
        { label: 'Inpatient Stay Authorization Review', pageId: 72869, slug: 'inpatient-stay-authorization-review', icon: 'fa fa-clipboard-check' },
      ],
    },
    {
      label: 'Customer Service & Communications',
      icon: 'fa fa-headset',
      children: [
        // CSR roles' landing page (HOME_BY_ROLE) — was routed + RBAC-gated but
        // had no nav link, so CSRs landed on a page missing from their menu.
        { label: 'Customer Service Desk', pageId: 72850, slug: 'customer-service-desk', icon: 'fa fa-headset' },
        { label: 'Notification Templates & Delivery Rules', pageId: 72872, slug: 'notification-channels-templates-delivery-rules', icon: 'fa fa-bell' },
        { label: 'Contact Center Integrations', pageId: 72882, slug: 'communication-gateway-contact-center-integrations', icon: 'fa fa-phone-volume' },
      ],
    },
    {
      label: 'Finance & Payouts',
      icon: 'fa fa-university',
      children: [
        { label: 'Finance Disbursements & Tax Panel', pageId: 72859, slug: 'finance-disbursements-tax-panel', icon: 'fa fa-money-check-alt' },
        { label: 'Carrier Funds & Billing Reconciliation', pageId: 72864, slug: 'carrier-funds-corporate-billing-reconciliation', icon: 'fa fa-file-invoice-dollar' },
        { label: 'Member Reimbursement Payouts', pageId: 72865, slug: 'member-reimbursement-payout-execution', icon: 'fa fa-hand-holding-usd' },
        { label: 'Payout Service Integration Management', pageId: 72874, slug: 'payout-service-integration-management', icon: 'fa fa-plug' },
      ],
    },
    {
      label: 'Reports & Analytics',
      icon: 'fa fa-chart-bar',
      children: [
        { label: 'Financial & Claims Reconciliation Reports', pageId: 72873, slug: 'financial-claims-paid-reconciliation-reports', icon: 'fa fa-chart-pie' },
        { label: 'Regulatory & Compliance Reports', pageId: 72876, slug: 'regulatory-audit-compliance-reports', icon: 'fa fa-clipboard-list' },
        { label: 'Operational SLA & Turnaround Reports', pageId: 72881, slug: 'operational-sla-queue-turnaround-reports', icon: 'fa fa-tachometer-alt' },
      ],
    },
  ],
  defaultMenuItem: { label: 'Dashboard', pageId: 72851, slug: 'member-portal-dashboard', icon: 'fa fa-home' },
};

// Flat lookup of every leaf (real page) entry, keyed by slug.
export const menuPagesBySlug = menuData.children
  .flatMap((group) => group.children || [])
  .reduce((acc, item) => {
    acc[item.slug] = item;
    return acc;
  }, {});

export default menuData;
